export const ROLE_LABELS = {
  owner: "Власник",
  admin: "Керівник",
  member: "Рієлтор",
};

export const MEMBERSHIP_STATUS_LABELS = {
  active: "Активний",
  suspended: "Призупинений",
  ended: "Завершений",
};

export const COMPANY_STATUS_LABELS = {
  active: "Активна",
  archived: "В архіві",
};

export const INVITATION_STATUS_LABELS = {
  pending: "Очікує",
  accepted: "Прийнято",
  declined: "Відхилено",
  revoked: "Відкликано",
  expired: "Закінчилось",
  superseded: "Замінено",
};

export const JOIN_REQUEST_STATUS_LABELS = {
  pending: "Очікує",
  approved: "Схвалено",
  rejected: "Відхилено",
  cancelled: "Скасовано",
  superseded: "Замінено",
};

export const roleLabel = (role) => ROLE_LABELS[role] || role || "—";

export const membershipStatusLabel = (status) =>
  MEMBERSHIP_STATUS_LABELS[status] || status || "—";

export const companyStatusLabel = (status) =>
  COMPANY_STATUS_LABELS[status] || status || "—";

export const invitationStatusLabel = (status) =>
  INVITATION_STATUS_LABELS[status] || status || "—";

export const joinRequestStatusLabel = (status) =>
  JOIN_REQUEST_STATUS_LABELS[status] || status || "—";

export const membershipRole = (company, membership) =>
  membership?.role || company?.my_role || null;

export const membershipStatusOf = (company, membership) =>
  membership?.status || company?.my_membership?.status || null;

/** ACTIVE membership with a role — independent of company archived/active. */
export const isActiveMembership = (company, membership) => {
  const status = membershipStatusOf(company, membership);
  const role = membershipRole(company, membership);
  return Boolean(company && status === "active" && role);
};

export const isOwnerMembership = (company, membership) =>
  isActiveMembership(company, membership) &&
  membershipRole(company, membership) === "owner";

export const isAdminMembership = (company, membership) => {
  const role = membershipRole(company, membership);
  return (
    isActiveMembership(company, membership) &&
    (role === "owner" || role === "admin")
  );
};

/** Ordinary admin writes require an active company (settings/profile/new invite). */
export const canOrdinaryAdminWrite = (company, membership) =>
  Boolean(company?.status === "active" && isAdminMembership(company, membership));

/** @deprecated Prefer canOrdinaryAdminWrite / isAdminMembership explicitly. */
export const isActiveAdmin = canOrdinaryAdminWrite;

/** @deprecated Prefer isOwnerMembership + company status checks. */
export const isActiveOwner = (company, membership) =>
  Boolean(company?.status === "active" && isOwnerMembership(company, membership));

export const isActiveMember = isActiveMembership;

export const canArchiveCompany = (company, membership) =>
  Boolean(company?.status === "active" && isOwnerMembership(company, membership));

export const companyAllowsEnrollment = (company) =>
  Boolean(company && company.status === "active");

/** B1 archive exception: transfer when OWNER membership is ACTIVE. */
export const canTransferOwnership = (company, membership) =>
  Boolean(
    isOwnerMembership(company, membership) &&
      (company.status === "active" || company.status === "archived"),
  );

/** Self-leave is always server-governed; UI may offer for active membership. */
export const canOfferSelfLeave = (company, membership) =>
  Boolean(isActiveMembership(company, membership));

/**
 * Active-company OWNER cannot leave without transfer — UI must CTA to transfer.
 * Archived OWNER leave remains a real confirm (server finalization).
 */
export const ownerLeaveRequiresTransfer = (company, membership) =>
  Boolean(
    company?.status === "active" && isOwnerMembership(company, membership),
  );

export const canManageMemberTarget = (actorRole, target) => {
  if (!target || target.role === "owner") return false;
  if (actorRole === "owner") {
    return target.role === "admin" || target.role === "member";
  }
  if (actorRole === "admin") {
    return target.role === "member";
  }
  return false;
};

/** Emergency suspend: ACTIVE ADMIN membership; company active or archived. */
export const canSuspendMemberTarget = (company, membership, target) => {
  if (!isAdminMembership(company, membership)) return false;
  if (!(company.status === "active" || company.status === "archived")) {
    return false;
  }
  if (!target || target.status !== "active") return false;
  return canManageMemberTarget(membershipRole(company, membership), target);
};

/** Resume only on active company + manage authority for target role. */
export const canResumeMemberTarget = (company, membership, target) => {
  if (!companyAllowsEnrollment(company)) return false;
  if (!isAdminMembership(company, membership)) return false;
  if (!target || target.status !== "suspended") return false;
  return canManageMemberTarget(membershipRole(company, membership), target);
};

export const canRemoveMemberTarget = (company, membership, target) => {
  if (!companyAllowsEnrollment(company)) return false;
  if (!isAdminMembership(company, membership)) return false;
  if (!target || target.status === "ended" || target.role === "owner") {
    return false;
  }
  return canManageMemberTarget(membershipRole(company, membership), target);
};

export const canEditMemberTarget = (company, membership, target) => {
  if (!companyAllowsEnrollment(company)) return false;
  if (!isAdminMembership(company, membership)) return false;
  if (!target || target.status !== "active") return false;
  return canManageMemberTarget(membershipRole(company, membership), target);
};

export const canGrantFinance = (company, membership, target) =>
  Boolean(
    company?.status === "active" &&
      isOwnerMembership(company, membership) &&
      target?.role === "admin" &&
      target?.status === "active",
  );

/** Archive exception: revoke allowed for OWNER while archived. */
export const canRevokeFinance = (company, membership, target) =>
  Boolean(
    isOwnerMembership(company, membership) &&
      (company.status === "active" || company.status === "archived") &&
      target?.role === "admin" &&
      target?.can_access_company_finance,
  );

export const canTransferToMember = (company, membership, target, selfMembershipId) =>
  Boolean(
    canTransferOwnership(company, membership) &&
      target &&
      target.status === "active" &&
      target.role !== "owner" &&
      target.id !== selfMembershipId,
  );

/** Invitation management list / create require ordinary admin on active company. */
export const canManageInvitations = canOrdinaryAdminWrite;

export const canInviteAdminRole = (company, membership) =>
  Boolean(canOrdinaryAdminWrite(company, membership) && isOwnerMembership(company, membership));

export const invitationRoleOptionsFor = (actorRole) => {
  const member = { value: "member", label: ROLE_LABELS.member };
  const admin = { value: "admin", label: ROLE_LABELS.admin };
  if (actorRole === "owner") return [member, admin];
  if (actorRole === "admin") return [member];
  return [];
};

/** Live pending invitations are actionable for copy/revoke. */
export const isLivePendingInvitation = (invitation) =>
  Boolean(
    invitation &&
      invitation.status === "pending" &&
      invitation.is_active_now !== false &&
      invitation.is_active_now !== 0,
  );

/**
 * Expired-pending (scheduler may not have flipped status yet) → reissue path.
 * Terminal statuses also use reissue when eligible.
 */
export const isExpiredPendingInvitation = (invitation) =>
  Boolean(
    invitation &&
      invitation.status === "pending" &&
      (invitation.is_active_now === false || invitation.is_active_now === 0),
  );

export const isTerminalInvitation = (invitation) =>
  Boolean(
    invitation &&
      ["expired", "declined", "revoked", "superseded"].includes(invitation.status),
  );

/**
 * Actor + invitation target role authority for management actions.
 * OWNER may manage ADMIN/MEMBER invitations; ADMIN may manage MEMBER only.
 */
export const canManageInvitationTarget = (company, membership, invitation) => {
  if (!canManageInvitations(company, membership) || !invitation) return false;
  const actorRole = membershipRole(company, membership);
  if (actorRole === "owner") {
    return invitation.role === "admin" || invitation.role === "member";
  }
  if (actorRole === "admin") {
    return invitation.role === "member";
  }
  return false;
};

export const canReissueInvitation = (company, membership, invitation) =>
  Boolean(
    canManageInvitationTarget(company, membership, invitation) &&
      (isExpiredPendingInvitation(invitation) ||
        isTerminalInvitation(invitation)),
  );

export const canCopyOrRevokeInvitation = (company, membership, invitation) =>
  Boolean(
    canManageInvitationTarget(company, membership, invitation) &&
      isLivePendingInvitation(invitation),
  );

export const invitationActionableForRecipient = (invitation) =>
  Boolean(
    invitation &&
      invitation.status === "pending" &&
      invitation.is_active_now &&
      invitation.company?.status !== "archived",
  );

export const memberDisplayName = (member) => {
  const user = member?.user;
  if (!user) return "Учасник";
  return user.fullname || user.handle || user.email || `Користувач #${user.id}`;
};

export const invitationLandingPath = (token) =>
  `/companies/invitations/by-token/${token}`;

export const errorCode = (err) => {
  const data = err?.response?.data;
  if (data && typeof data.code === "string") return data.code;
  return null;
};

export const committedEnrollmentStatus = (err, key) => {
  const data = err?.response?.data;
  if (data && typeof data[key] === "string") return data[key];
  return null;
};

/** Terminal join_request statuses that mean "already finished", not pending conflict. */
export const isTerminalJoinRequestStatus = (status) =>
  ["approved", "rejected", "cancelled", "superseded"].includes(status);
